/**
 * Bucket-by-position review model for the pre-gameweek-one "build your team"
 * flow on /my-team/pick.
 *
 * Ported from the scoreline-app confirm-imported-team screen. Two entry points
 * feed it: a screenshot import (0-15 matched players) and a manual build
 * (empty). Rather than slotting players 1-15 and hoping the squad is valid, the
 * build screen groups them by FPL position and shows each row with its target
 * size. The user fills empty slots and removes overflow until the squad is
 * 2 GK + 5 DEF + 5 MID + 3 FWD.
 *
 * Slot numbers (the planner's 1-15 contract) are derived only at submit time
 * via `toSlotOrderedPicks`. The review state itself is position-keyed.
 */

export type BuildPosition = 'GK' | 'DEF' | 'MID' | 'FWD';

/**
 * Minimal player shape shared by screenshot matches (the import-team response)
 * and players chosen from the picker (the /league/players `Player`). Both carry
 * everything the build pitch needs to render and to submit to the planner.
 */
export type BuildPlayer = {
  id: string;
  displayName: string;
  position: BuildPosition;
  team: { shortName: string };
  /** CDN-hosted team kit image from the ffh-backend API, when available. */
  kitImageUrl?: string;
  price: number;
};

export const REQUIRED: Record<BuildPosition, number> = {
  GK: 2,
  DEF: 5,
  MID: 5,
  FWD: 3
} as const;

export const POSITIONS = ['GK', 'DEF', 'MID', 'FWD'] as const;

export type Formation = { DEF: number; MID: number; FWD: number };

/**
 * Fallback used when the input isn't 15 players or isn't a valid FPL formation
 * (e.g. wrong squad composition). Drives the starters/bench split; the user can
 * adjust post-save in the planner.
 */
export const DEFAULT_STARTING: Formation = { DEF: 4, MID: 4, FWD: 2 } as const;

const STARTING_XI_SIZE = 11;
const MIN_DEF = 3;
const MAX_DEF = 5;
const MIN_MID = 2;
const MAX_MID = 5;
const MIN_FWD = 1;
const MAX_FWD = 3;

const isValidFormation = (formation: Formation): boolean => {
  const totalOutfield = formation.DEF + formation.MID + formation.FWD;
  return (
    totalOutfield === STARTING_XI_SIZE - 1 &&
    formation.DEF >= MIN_DEF &&
    formation.DEF <= MAX_DEF &&
    formation.MID >= MIN_MID &&
    formation.MID <= MAX_MID &&
    formation.FWD >= MIN_FWD &&
    formation.FWD <= MAX_FWD
  );
};

/**
 * Derive the starting formation from input slots 2..11 (the starting
 * outfielders in pitch-view order). Falls back to `DEFAULT_STARTING` if the
 * input is malformed - the user is going to fix the squad anyway in that case.
 */
const deriveFormation = (matched: readonly BuildPlayer[]): Formation => {
  if (matched.length < STARTING_XI_SIZE) return DEFAULT_STARTING;

  const startingOutfield = matched.slice(1, STARTING_XI_SIZE);
  const candidate: Formation = {
    DEF: startingOutfield.filter((player) => player.position === 'DEF').length,
    MID: startingOutfield.filter((player) => player.position === 'MID').length,
    FWD: startingOutfield.filter((player) => player.position === 'FWD').length
  };
  return isValidFormation(candidate) ? candidate : DEFAULT_STARTING;
};

export type ReviewSlot = BuildPlayer | null;

export type ReviewLineup = {
  formation: Formation;
  GK: ReviewSlot[];
  DEF: ReviewSlot[];
  MID: ReviewSlot[];
  FWD: ReviewSlot[];
};

const buildRow = (
  players: BuildPlayer[],
  position: BuildPosition
): ReviewSlot[] => {
  const required = REQUIRED[position];
  // Preserve input order: the OCR pipeline returns players in pitch-view
  // reading order, so the first GK is the starter, the second is the bench GK.
  // Surplus matches beyond REQUIRED are dropped; the user resolves duplicates
  // by removing on the pitch and picking the correct player.
  const taken = players.slice(0, required);
  const padding: ReviewSlot[] = Array.from(
    { length: required - taken.length },
    () => null
  );
  return [...taken, ...padding];
};

/** Group matched players into position rows; surplus per position is dropped. */
export const buildReviewLineup = (
  matched: readonly BuildPlayer[]
): ReviewLineup => {
  const byPosition: Record<BuildPosition, BuildPlayer[]> = {
    GK: [],
    DEF: [],
    MID: [],
    FWD: []
  };
  for (const player of matched) byPosition[player.position].push(player);

  return {
    formation: deriveFormation(matched),
    GK: buildRow(byPosition.GK, 'GK'),
    DEF: buildRow(byPosition.DEF, 'DEF'),
    MID: buildRow(byPosition.MID, 'MID'),
    FWD: buildRow(byPosition.FWD, 'FWD')
  };
};

export type CompositionCounts = Record<BuildPosition, number>;

/** Per-position counts of filled slots. */
export const composition = (lineup: ReviewLineup): CompositionCounts => ({
  GK: lineup.GK.filter((slot): slot is BuildPlayer => slot !== null).length,
  DEF: lineup.DEF.filter((slot): slot is BuildPlayer => slot !== null).length,
  MID: lineup.MID.filter((slot): slot is BuildPlayer => slot !== null).length,
  FWD: lineup.FWD.filter((slot): slot is BuildPlayer => slot !== null).length
});

/** True when the squad is exactly 2/5/5/3. Captain/vice checked separately. */
export const isReviewLineupValid = (lineup: ReviewLineup): boolean => {
  const counts = composition(lineup);
  return (
    counts.GK === REQUIRED.GK &&
    counts.DEF === REQUIRED.DEF &&
    counts.MID === REQUIRED.MID &&
    counts.FWD === REQUIRED.FWD
  );
};

type SlotLocation = { position: BuildPosition; index: number };

/** Replace the slot at (position, index). Pass `null` to clear it. */
export const setSlot = (
  lineup: ReviewLineup,
  location: SlotLocation,
  player: BuildPlayer | null
): ReviewLineup => ({
  ...lineup,
  [location.position]: lineup[location.position].map((existing, index) =>
    index === location.index ? player : existing
  )
});

/** Returns the (position, index) location of a player on the pitch, or null. */
export const findLocation = (
  lineup: ReviewLineup,
  playerId: string
): SlotLocation | null => {
  for (const position of POSITIONS) {
    const index = lineup[position].findIndex((slot) => slot?.id === playerId);
    if (index !== -1) return { position, index };
  }
  return null;
};

/**
 * Every filled-slot player on the pitch, in position order. Their ids drive the
 * picker's exclude list, and their clubs the per-club limit check.
 */
export const allSquadPlayers = (lineup: ReviewLineup): BuildPlayer[] => {
  const players: BuildPlayer[] = [];
  for (const position of POSITIONS) {
    for (const slot of lineup[position]) {
      if (slot) players.push(slot);
    }
  }
  return players;
};

/** Total price of all filled slots, used to show the running bank. */
export const totalSquadPrice = (lineup: ReviewLineup): number => {
  let total = 0;
  for (const position of POSITIONS) {
    for (const slot of lineup[position]) {
      if (slot) total += slot.price;
    }
  }
  return total;
};

const collectStarters = (lineup: ReviewLineup): BuildPlayer[] => {
  const formation = lineup.formation;
  return [
    ...(lineup.GK[0] ? [lineup.GK[0]] : []),
    ...lineup.DEF.slice(0, formation.DEF).filter(
      (slot): slot is BuildPlayer => slot !== null
    ),
    ...lineup.MID.slice(0, formation.MID).filter(
      (slot): slot is BuildPlayer => slot !== null
    ),
    ...lineup.FWD.slice(0, formation.FWD).filter(
      (slot): slot is BuildPlayer => slot !== null
    )
  ];
};

/** Most expensive starter - the default captain pick once the squad is valid. */
export const pickDefaultCaptain = (lineup: ReviewLineup): string | null => {
  const sorted = collectStarters(lineup).sort(
    (one, two) => two.price - one.price
  );
  return sorted[0] ? sorted[0].id : null;
};

/** Most expensive starter who isn't the captain - the default vice pick. */
export const pickDefaultViceCaptain = (
  lineup: ReviewLineup,
  captainId: string | null
): string | null => {
  const sorted = collectStarters(lineup)
    .filter((player) => player.id !== captainId)
    .sort((one, two) => two.price - one.price);
  return sorted[0] ? sorted[0].id : null;
};

/**
 * Maps each (position, index) slot to the FPL slot convention 1-15:
 *   1                              : starting GK
 *   2 .. 1+DEF                     : starting DEFs
 *   2+DEF .. 1+DEF+MID             : starting MIDs
 *   2+DEF+MID .. 11                : starting FWDs
 *   12                             : bench GK (FPL fixes the reserve keeper
 *                                    to the first bench slot)
 *   13 / 14 / 15                   : bench outfielders (DEF -> MID -> FWD)
 */
export const buildSlotMap = (
  formation: Formation
): Record<BuildPosition, number[]> => {
  const slots: Record<BuildPosition, number[]> = {
    GK: [],
    DEF: [],
    MID: [],
    FWD: []
  };

  slots.GK.push(1);
  let starterSlot = 2;
  for (let i = 0; i < formation.DEF; i++) slots.DEF.push(starterSlot++);
  for (let i = 0; i < formation.MID; i++) slots.MID.push(starterSlot++);
  for (let i = 0; i < formation.FWD; i++) slots.FWD.push(starterSlot++);

  // Bench GK first — matching the planner's FPL-sourced squads and how the
  // bench is rendered everywhere (first sub = reserve keeper).
  slots.GK.push(12);

  let benchSlot = 13;
  for (let i = formation.DEF; i < REQUIRED.DEF; i++)
    slots.DEF.push(benchSlot++);
  for (let i = formation.MID; i < REQUIRED.MID; i++)
    slots.MID.push(benchSlot++);
  for (let i = formation.FWD; i < REQUIRED.FWD; i++)
    slots.FWD.push(benchSlot++);

  return slots;
};

const must = (slot: ReviewSlot, label: string): BuildPlayer => {
  if (!slot) throw new Error(`Cannot submit: ${label} is empty`);
  return slot;
};

export type SquadPick = {
  id: string;
  isCaptain: boolean;
  isViceCaptain: boolean;
};

/**
 * Maps the position-keyed review state into the planner's 1-15 slot order.
 * Throws if any required slot is empty - the caller should already have gated
 * submit on `isReviewLineupValid`.
 */
export const toSlotOrderedPicks = (
  lineup: ReviewLineup,
  captainId: string,
  viceCaptainId: string
): SquadPick[] => {
  const formation = lineup.formation;
  const ordered: BuildPlayer[] = [];

  ordered.push(must(lineup.GK[0], 'starting GK'));
  for (let i = 0; i < formation.DEF; i++) {
    ordered.push(must(lineup.DEF[i], `starting DEF ${i + 1}`));
  }
  for (let i = 0; i < formation.MID; i++) {
    ordered.push(must(lineup.MID[i], `starting MID ${i + 1}`));
  }
  for (let i = 0; i < formation.FWD; i++) {
    ordered.push(must(lineup.FWD[i], `starting FWD ${i + 1}`));
  }

  ordered.push(must(lineup.GK[1], 'bench GK'));
  for (let i = formation.DEF; i < REQUIRED.DEF; i++) {
    ordered.push(must(lineup.DEF[i], 'bench DEF'));
  }
  for (let i = formation.MID; i < REQUIRED.MID; i++) {
    ordered.push(must(lineup.MID[i], 'bench MID'));
  }
  for (let i = formation.FWD; i < REQUIRED.FWD; i++) {
    ordered.push(must(lineup.FWD[i], 'bench FWD'));
  }

  return ordered.map((player) => ({
    id: player.id,
    isCaptain: player.id === captainId,
    isViceCaptain: player.id === viceCaptainId
  }));
};
